import { Component, Input, OnInit, forwardRef, ViewChild, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';

@Component({
    standalone: false,
    selector: 'res-custom-date-time-range-picker',
    templateUrl: './custom-date-time-range-picker.component.html',
    styleUrls: ['./custom-date-time-range-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomDateTimeRangePickerComponent),
            multi: true
        }
    ]
})
export class CustomDateTimeRangePickerComponent implements OnInit {
    @ViewChild('errorPopover', { static: true }) errorPopover: PopoverDirective;
    @Input() pickerConfig: Partial<BsDatepickerConfig>;
    @Input() automationId = '';

    private _filterItem: any;
    get filterItem(): any {
        return this._filterItem;
    }
    @Input()
    set filterItem(value: any) {
        this._filterItem = value;
        if (!value) {
            this.leftDatePicker = this.rightDatePicker = new Date();
            setTimeout(() => {
                this.startYear = this.endYear = this.startHour = this.endHour = this.leftDatePicker = this.rightDatePicker = null;
            });
        } else {
            this.setFilterDates(value);
        }
    }

    @Input() onPickerApplyClick: (item: any) => void;
    @Input() onPickerCancelClick: () => void;

    isLeftDateInvalid = false;
    isRightDateInvalid = false;
    inputFocused: boolean;
    pickerError = "Invalid Date";
    leftDatePicker: any;
    rightDatePicker: any;
    startYear: any;
    endYear: any;
    startHour: string;
    endHour: string;
    placeholderField1 = 'On/after date ...';
    placeholderField2 = 'Time (24hr) ...';
    placeholderField3 = 'On/before date ...';
    focusedDatePlaceholder = 'YYYY-MM-DD';
    focusedTimePlaceholder = 'HH:mm:ss';

    private defaultConfig: Partial<BsDatepickerConfig>;

    constructor(public changeDetector: ChangeDetectorRef) { }

    ngOnInit(): void {
        if (!this.pickerConfig) {
            this.defaultConfig = new BsDatepickerConfig();
            this.defaultConfig.showWeekNumbers = false;
            this.defaultConfig.dateInputFormat = 'YYYY-MM-DD';
            this.defaultConfig.containerClass = 'bs-datepicker-container-date-time-range';
            this.defaultConfig.adaptivePosition = true;
            this.defaultConfig.isAnimated = false;
            this.defaultConfig.customTodayClass = 'custom-today-class';
            this.pickerConfig = this.defaultConfig;
        }
        this.leftDatePicker = this.rightDatePicker = new Date();
        this.startYear = this.endYear = this.startHour = this.endHour = this.leftDatePicker = this.rightDatePicker = null;
    }

    onDatePickerLeftChange = (value?: Date) => {
        if (value) {
            this.isLeftDateInvalid = false;
            this.leftDatePicker = value;
            const selectedDate = new Date(value.getTime() + value.getTimezoneOffset() * 60 * 1000);
            this.startYear = selectedDate.getFullYear() + '-' + this.addZeroToTime((selectedDate.getMonth() + 1)) + '-' + this.addZeroToTime(selectedDate.getDate());
        }
    }

    onDatePickerRightChange = (value?: Date) => {
        if (value) {
            this.isRightDateInvalid = false;
            this.rightDatePicker = value;
            const selectedDate = new Date(value.getTime() + value.getTimezoneOffset() * 60 * 1000);
            this.endYear = selectedDate.getFullYear() + '-' + this.addZeroToTime((selectedDate.getMonth() + 1)) + '-' + this.addZeroToTime(selectedDate.getDate());
        }
    }

    addZeroToTime(time): any {
        if (time > 0 && time < 10 && time.toString().length == 1) {
            time = '0' + time;
        }
        if (time === 0) {
            time = '00';
        }
        return time;
    }

    onClearClick(type): void {
        switch (type) {
            case 'startYear':
                this.isLeftDateInvalid = false;
                this.startYear = this.leftDatePicker = null;
                break;
            case 'endYear':
                this.isRightDateInvalid = false;
                this.endYear = this.rightDatePicker = null;
                break;
            case 'startHour':
                this.startHour = null;
                break;
            case 'endHour':
                this.endHour = null;
                break;
        }
    }

    onPickerInputFocus(isFocused: boolean, event: any): void {
        this.inputFocused = isFocused;
        const input: any = event.currentTarget;
        if (isFocused) {
            this.isLeftDateInvalid = false;
            input.placeholder = event.currentTarget.id.indexOf('Year') !== -1 ? this.focusedDatePlaceholder : this.focusedTimePlaceholder;
        } else {
            switch (event.currentTarget.id) {
                case 'startYear':
                    input.placeholder = this.placeholderField1;
                    if (this.startYear && !this.isValidLeftDate(this.startYear)) {
                        this.setTimezoneDate(new Date(this.startYear), 'leftDatePicker');
                    }
                    break;
                case 'endYear':
                    input.placeholder = this.placeholderField3;
                    if (this.endYear && !this.isValidRightDate(this.endYear)) {
                        this.setTimezoneDate(new Date(this.endYear), 'rightDatePicker');
                    }
                    break;
                case 'startHour':
                    input.placeholder = this.placeholderField2;
                    break;
                case 'endHour':
                    input.placeholder = this.placeholderField2;
                    break;
            }
        }
    }

    isValidLeftDate(d: any): boolean {
        const selectedDate: any = new Date(d);
        return this.isLeftDateInvalid = isNaN(selectedDate) && selectedDate instanceof Date;
    }

    isValidRightDate(d: any): boolean {
        const selectedDate: any = new Date(d);
        return this.isRightDateInvalid = isNaN(selectedDate) && selectedDate instanceof Date;
    }

    setTimezoneDate(dateSelected, type): void {
        if (dateSelected.getTimezoneOffset() > 0) {
            const selectedDate: any = new Date(dateSelected.getTime() + dateSelected.getTimezoneOffset() * 60 * 1000);
            this[type] = new Date(selectedDate);
        } else {
            this[type] = dateSelected;
        }
    }

    setTodayDateTime(type): void {
        const currentDate = new Date();
        switch (type) {
            case 'startYear':
                this.startYear = currentDate.toISOString().slice(0, 10);
                this.setTimezoneDate(currentDate, 'leftDatePicker');
                break;
            case 'endYear':
                this.endYear = currentDate.toISOString().slice(0, 10);
                this.setTimezoneDate(currentDate, 'rightDatePicker');
                break;
            case 'startHour':
                this.startHour = new Date().toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric"
                });
                break;
            case 'endHour':
                this.endHour = new Date().toLocaleTimeString('en-US', {
                    hour12: false,
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric"
                });
                break;
        }
    }

    onSelectDateClick(period): void {
        this.startHour = "00:00:00";
        this.endHour = "23:59:59";
        switch (period) {
            case '-1': {
                const today = this.leftDatePicker = new Date();
                const yesterday = this.leftDatePicker.setDate(today.getDate() - 1);
                this.leftDatePicker = this.rightDatePicker = new Date(yesterday);
                break;
            }
            case '0': {
                this.leftDatePicker = this.rightDatePicker = new Date();
                break;
            }
            case '24': {
                const today = this.leftDatePicker = new Date();
                const yesterday = this.leftDatePicker.setDate(today.getDate() - 1);
                this.leftDatePicker = new Date(yesterday);
                this.setSpecificLeftRightDate(this.leftDatePicker);
                break;
            }
            case '7': {
                const today = this.leftDatePicker = new Date();
                const yesterday = this.leftDatePicker.setDate(today.getDate() - 7);
                this.leftDatePicker = new Date(yesterday);
                this.setSpecificLeftRightDate(this.leftDatePicker);
                break;
            }
            case '30': {
                const today = this.leftDatePicker = new Date();
                const yesterday = this.leftDatePicker.setDate(today.getDate() - 30);
                this.leftDatePicker = new Date(yesterday);
                this.setSpecificLeftRightDate(this.leftDatePicker);
                break;
            }
        }
        this.changeDetector.detectChanges();
    }

    setSpecificLeftRightDate(date): void {
        this.setTodayDateTime('startHour');
        this.setTodayDateTime('endHour');
        this.rightDatePicker = new Date();
    }

    onApplyClick(): void {

        if (this.isValidLeftDate(this.startYear) || this.isValidLeftDate(this.endYear)) {
            return;
        }

        if (this.startYear && !this.endYear && !this.startHour && !this.endHour) {
            this.startHour = "00:00:00";
            this.endHour = "23:59:59";
            this.leftDatePicker = new Date(this.startYear);
            this.rightDatePicker = new Date();
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (!this.startYear && this.endYear && !this.startHour && !this.endHour) {
            this.startHour = "00:00:00";
            this.leftDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);

            this.rightDatePicker = new Date(this.endYear);
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
            this.endHour = "23:59:59";
        }

        if (!this.startYear && !this.endYear && this.startHour && !this.endHour) {
            this.endHour = "23:59:59";
            this.leftDatePicker = this.rightDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (!this.startYear && !this.endYear && !this.startHour && this.endHour) {
            this.startHour = "00:00:00";
            this.leftDatePicker = this.rightDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (this.startYear && this.endYear && !this.startHour && !this.endHour) {
            this.startHour = "00:00:00";
            this.endHour = "23:59:59";
            this.leftDatePicker = new Date(this.startYear);
            this.rightDatePicker = new Date(this.endYear);
        }

        if (!this.startYear && !this.endYear && this.startHour && this.endHour) {
            this.leftDatePicker = this.rightDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (!this.startYear && this.endYear && this.startHour && !this.endHour) {
            this.leftDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);
            this.endHour = "23:59:59";
        }

        if (this.startYear && !this.endYear && !this.startHour && this.endHour) {
            this.startHour = "00:00:00";
            this.rightDatePicker = new Date();
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (!this.startYear && this.endYear && !this.startHour && this.endHour) {
            this.startHour = "00:00:00";
            this.leftDatePicker = new Date();
            this.startYear = this.leftDatePicker.toISOString().slice(0, 10);
        }

        if (this.startYear && !this.endYear && this.startHour && !this.endHour) {
            this.endHour = "23:59:59";
            this.rightDatePicker = new Date();
            this.endYear = this.rightDatePicker.toISOString().slice(0, 10);
        }

        if (this.startYear && this.endYear && !this.startHour && this.endHour) {
            this.startHour = "00:00:00";
        }

        if (this.startYear && this.endYear && this.startHour && !this.endHour) {
            this.endHour = "23:59:59";
        }

        const dateToFilter = this.startYear + ' ' + this.startHour + ' ~~ ' + this.endYear + ' ' + this.endHour;
        this.onPickerApplyClick(dateToFilter);
        this.changeDetector.detectChanges();
    }

    onCancelClick(): void {
        this.startYear = this.endYear = this.startHour = this.endHour = this.leftDatePicker = this.rightDatePicker = null;
        this.setFilterDates(this.filterItem);
        this.onPickerCancelClick();
    }

    setCalendarDate = (calendar) => {
        if (calendar === 'left' && this.rightDatePicker) {
            this.leftDatePicker = new Date(this.rightDatePicker);
            this.startYear = this.endYear;
            this.startHour = this.endHour;
        } else if (this.leftDatePicker) {
            this.rightDatePicker = new Date(this.leftDatePicker);
            this.endYear = this.startYear;
            this.endHour = this.startHour;
        }
    }

    setFilterDates(filterValue?: string): void {
        if (filterValue) {
            const filterDates = filterValue.split(' ~~ ');
            if (!this.isValidLeftDate(this.startYear)) {
                const date = new Date(filterDates[0].slice(0, 10));
                this.setTimezoneDate(date, 'leftDatePicker');
            }
            if (!this.isValidRightDate(this.endYear)) {
                const date = new Date(filterDates[1].slice(0, 10));
                this.setTimezoneDate(date, 'rightDatePicker');
            }
            setTimeout(() => {
                this.startYear = filterDates[0].slice(0, 10).trim();
                this.startHour = filterDates[0].slice(10, 20).trim();
                this.endYear = filterDates[1].slice(0, 10).trim();
                this.endHour = filterDates[1].slice(10, 20).trim();
                this.changeDetector.detectChanges();
            }, 100);
        }
    }
}
